
import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import Toast from '@/utils/toast';
import AppearanceSettings from '@/components/settings/AppearanceSettings.vue';
import BusinessSettings from '@/components/settings/BusinessSettings.vue';
import CreditsSettings from '@/components/settings/CreditsSettings.vue';
import FulfilmentSettings from '@/components/settings/FulfilmentSettings.vue';
import IntegrationSettings from '@/components/settings/IntegrationSettings.vue';
import OperationSettings from '@/components/settings/OperationSettings.vue';
import PromoSettings from '@/components/settings/PromoSettings.vue';
import SubsidiesSettings from '@/components/settings/SubsidiesSettings.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import Tabs from '@/components/shared/Tabs.vue';

const namespace: string = 'auth';

@Component<Settings>({
	components: {
		AppearanceSettings,
		BusinessSettings,
		CreditsSettings,
		FulfilmentSettings,
		IntegrationSettings,
		OperationSettings,
		PromoSettings,
		SubsidiesSettings,
		SuitesHeader,
		Tabs
	}
})

export default class Settings extends Vue {
	@Action('fetchRestaurant', { namespace }) private fetchRestaurant!: (payload: any) => Promise<Restaurant>;
	@Action('updateRestaurant', { namespace }) private updateRestaurant!: (restaurant: Restaurant | object) => Promise<void>;
	@Action('uploadRestaurantImage', { namespace }) private uploadRestaurantImageAction!: (payload: object) => Promise<void>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('getTenantId', { namespace }) private tenantId!: string;
	@Getter('areApp8CreditsEnabled', { namespace }) private areApp8CreditsEnabled!: boolean;
	@Getter('hasSubsidies', { namespace }) private hasSubsidies!: boolean;
	@Getter('isDashboardEnabled', { namespace }) private isDashboardEnabled!: boolean;
	@Getter('isK12Location', { namespace }) private isK12Location!: boolean;
	@Getter('isCateringRestaurant', { namespace }) private isCateringRestaurant!: Boolean;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;

	private loading: boolean = false;
	private PERMISSION = PERMISSION;
	private savingSettings: boolean = false;
	private refMap: Map<string, string> = new Map<string, string>([
		['operations', 'operationsSettings'],
		['fulfilment', 'fulfilmentSettings'],
		['appearance', 'appearanceSettings'],
		['promos', 'promoSettings'],
		['business-settings', 'businessSettings'],
		['subsidies', 'subsidiesSettings'],
		['credits', 'creditsSettings'],
		['integrations', 'integrationSettings']
	]);
	// Initialized with a safe placeholder; the real tab is resolved in created()
	// once Vuex getters (this.hasPermission) backing the `tabs` computed are available.
	private currentTab: Tab = { key: 'operations', label: '' };

	$refs!: {
		[key: string]: OperationSettings | FulfilmentSettings | AppearanceSettings | PromoSettings | BusinessSettings | IntegrationSettings | Tabs;
	};

	private get tabs(): Tab[] {
		return [
			{ key: 'operations', label: this.$t('settings.tabs.operations') },
			{ key: 'fulfilment', label: this.$t('settings.tabs.fulfilment'), hidden: !this.hasPermission(PERMISSION.SET_DELIVERY) },
			{ key: 'appearance', label: this.$t('settings.tabs.appearance'), hidden: !this.hasPermission(PERMISSION.SET_APPEARANCE) },
			{ key: 'promos', label: this.$t('settings.tabs.promos'), hidden: !this.hasPermission(PERMISSION.SET_PROMO_CODES) },
			{ key: 'business-settings', label: this.$t('settings.tabs.business_settings'), hidden: !this.hasPermission(PERMISSION.SET_BUSINESS_SETTINGS) },
			{ key: 'integrations', label: this.$t('settings.tabs.integrations'), hidden: !this.hasPermission(PERMISSION.SET_INTEGRATIONS) || !this.isDashboardEnabled || !this.isCateringRestaurant || this.isK12Location },
			{ key: 'subsidies', label: this.$t('settings.tabs.subsidies'), hidden: !this.hasPermission(PERMISSION.SET_SUBSIDIES) || !this.hasSubsidies },
			{ key: 'credits', label: this.$t('settings.tabs.credits'), hidden: !this.hasPermission(PERMISSION.SET_ASSIGN_CREDITS) || !this.areApp8CreditsEnabled }
		];
	}

	private get initialTab(): string {
		const hash = window.location.hash.replace('#', '');
		const tab = this.tabs.find(tab => tab.key === hash && !tab.hidden);
		return tab ? tab.key : this.tabs[0].key;
	}

	private get showSaveBtn(): boolean {
		return !['subsidies', 'credits', 'integrations'].includes(this.currentTab.key);
	}

	private async created(): Promise<void> {
		this.currentTab = this.tabs.find(tab => tab.key === this.initialTab) || this.tabs[0];
		await this.getRestaurant();
	}

	private mounted(): void {
		this.positionSaveButton();
	}

	private async getRestaurant(): Promise<void> {
		try {
			this.loading = true;
			await this.fetchRestaurant({
				tenantId: this.tenantId
			});
		}
		catch (error) {
			Toast.error(this.$t('settings.error_fetching', { restaurantName: this.restaurant.name, errorMessage: error }));
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Call the save function in the corresponding settings component
	 *
	 * @return {Promise<void>}
	 */
	private async save(): Promise<void> {
		const refName = this.refMap.get(this.currentTab.key);
		if (!refName) {
			return;
		}
		const componentRef = this.$refs[refName];
		if (!componentRef) {
			return;
		}
		await (componentRef as OperationSettings | FulfilmentSettings | AppearanceSettings | PromoSettings | BusinessSettings).save();
	}

	/**
	 * Save the settings for the current tab
	 * This function is called when save event is emitted from the child settings component
	 *
	 * @param {Restaurant} updatedRestaurant
	 * @param {any} additionalData
	 * @return {Promise<void>}
	 */
	private async handleSaveData(updatedRestaurant: Restaurant, additionalData?: any): Promise<void> {
		try {
			if (updatedRestaurant.discount) {
				this.setDiscountForSave(updatedRestaurant);
			}

			this.savingSettings = true;
			await this.updateRestaurant({
				restaurant: updatedRestaurant
			});

			// Handle image upload if provided (for appearance settings)
			if (additionalData?.imageFile) {
				await this.uploadRestaurantImageAction({
					restaurant: updatedRestaurant,
					image: additionalData.imageFile
				});
			}

			Toast.success(this.$t('settings.success'));
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.error_updating', { restaurantName: this.restaurant.name, errorMessage }));
		}
		finally {
			this.savingSettings = false;
		}
	}

	/**
	 * Add symbol at the end of the discount values before saving (%, $)
	 * ** FOR NOW IT'S ONLY % **
	 *
	 * @return {void}
	 */
	private setDiscountForSave(updatedRestaurant: Restaurant): void {
		if (updatedRestaurant.discount?.codes?.length) {
			// Clear out any empty values
			updatedRestaurant.discount.codes = updatedRestaurant.discount.codes.filter((code: DiscountInfoCodes) => {
				return code.name && code.value;
			});

			// Add the symbol
			updatedRestaurant.discount.codes.forEach((code: DiscountInfoCodes) => {
				code.value = `${code.value.replace(/%/g, '')}%`;
			});
		}
	}

	private changeTab(tabKey: string): void {
		const tab = this.tabs.find(tab => tab.key === tabKey);
		if (tab && !tab.hidden) {
			(this.$refs.tabs as Tabs).setTab(tabKey);
			this.currentTab = tab;
		}
	}

	/**
	 * Position save button depending on the drawer on load
	 *
	 * @return {void}
	 */
	private positionSaveButton(): void {
		setTimeout(() => {
			const drawer = document.getElementById('nav-drawer');
			if(drawer) {
				document.documentElement.style.setProperty('--dynamic-btn-left-value', `${drawer.offsetWidth}px`);
			}
		}, 200);
	}
}
